/**
 * "Metres run" per casing string, on a ticket (api#830). One input per string
 * on the ticket's job, labelled with the combo that tells the strings apart,
 * and the ticket's TOTAL as the API last saved it.
 *
 * The total is never added up here — see lib/ticket-string-metres.ts. Until the
 * ticket is saved, the server's figure is the last saved one, and the line says
 * so rather than pretending to be live.
 */
import type { JobCasingString } from '../../types/casing-string';
import { fmtQty } from '../../lib/display-labels';
import { casingStringLabel } from '../../lib/casing-strings-display';
import { stringsThatTakeMetres, totalIsStringSum, type MetresDraft, type RecordedMetres } from '../../lib/ticket-string-metres';

interface Props {
  strings: JobCasingString[];
  draft: MetresDraft;
  onChange: (casingStringId: string, value: string) => void;
  recorded: RecordedMetres | null;
  /** The ticket's `actual_metres_run`, as the API returned it. */
  totalFromApi: unknown;
  inputClassName: string;
  labelClassName: string;
  disabled?: boolean;
}

export default function CasingStringMetresFields({
  strings, draft, onChange, recorded, totalFromApi, inputClassName, labelClassName, disabled,
}: Props) {
  const withIds = stringsThatTakeMetres(strings);
  if (withIds.length === 0) return null;
  const qty = fmtQty(totalFromApi as any);
  const total = qty === '—' ? null : `${qty} m`;
  const isSum = totalIsStringSum(recorded);

  return (
    <div className="space-y-2" data-testid="casing-string-metres">
      {withIds.map((s) => {
        const inputId = `metres-${s.id}`;
        return (
          <div key={s.id}>
            <label htmlFor={inputId} className={labelClassName}>
              Metres run — {casingStringLabel(s)}
            </label>
            <input
              id={inputId}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              disabled={disabled}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              value={draft[s.id] ?? ''}
              onChange={(e) => onChange(s.id, e.target.value)}
              placeholder="Metres"
              className={inputClassName}
            />
          </div>
        );
      })}
      <p className="text-xs text-fieldiq-muted" data-testid="casing-string-metres-total">
        {isSum && total
          ? <>Total metres run: <span className="text-fieldiq-text font-medium">{total}</span> — the sum of the strings as last saved. It updates when you save.</>
          : 'Total metres run is the sum of the strings, worked out when you save.'}
      </p>
    </div>
  );
}
